import React, { useRef, useState, useEffect } from "react";
import { withRouter } from "react-router-dom";
import { FaArrowLeft } from "react-icons/fa";
import { withUser } from "../Contexts/user";
import html2canvas from "html2canvas";

const NameCard = ({ user, history }) => {
    const frontCardRef = useRef(null);
    const backCardRef = useRef(null);
    const desktopScaleRef = useRef(null);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    /** Desktop only: preview scale (native card is 800px; cap below 1 so preview stays compact). */
    const [desktopCardScale, setDesktopCardScale] = useState(() =>
        typeof window !== "undefined" && window.innerWidth > 768 ? 0.78 : 1
    );

    const frontimage =
        user.auth.state.name === "Melaka"
            ? "https://ik.imagekit.io/dnddecpho/dndream/D&D_2025NameCard_Template5_7pTXNP4TG.jpg?updatedAt=1750427635224"
            : user.auth.state.name === "Penang"
            ? "https://ik.imagekit.io/dnddecpho/dndream/D&D_2025NameCard_Template6_rVAClWqfC.jpg?updatedAt=1750427635588"
            : user.auth.state.name === "Ipoh"
            ? "https://ik.imagekit.io/dnddecpho/dndream/D&D_2025NameCard_Template4_8dsdNgda9.jpg?updatedAt=1750427635233"
            : user.auth.state.name === "Johor Bahru"
            ? "https://ik.imagekit.io/dnddecpho/dndream/D&D_2025NameCard_Template3_20cZr46iO.jpg?updatedAt=1750427635505"
            : "https://ik.imagekit.io/dnddecpho/dndream/D&D_2025NameCard_Template2_v-m4igqDr.jpg?updatedAt=1749152219108";

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (isMobile) {
            setDesktopCardScale(1);
            return;
        }
        const el = desktopScaleRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;

        const updateScale = () => {
            const w = el.getBoundingClientRect().width;
            if (w <= 0) return;
            const fit = w / 800;
            const maxPreview = 0.78;
            const minPreview = 0.62;
            const clamped = Math.min(maxPreview, Math.max(minPreview, fit));
            setDesktopCardScale(clamped);
        };

        updateScale();
        const ro = new ResizeObserver(updateScale);
        ro.observe(el);
        return () => ro.disconnect();
    }, [isMobile]);

    const capitalizeWords = str => {
        return str.toLowerCase().replace(/\b\w/g, char => char.toUpperCase());
    };

    const formatRankLabel = (value) =>
        value
            .split(" ")
            .map(
                (word) =>
                    word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
            )
            .join(" ");

    const getDirectorDisplayTitle = (director) => {
        if (director === "SD") return "Sales Director";
        if (director === "PD") return "Project Director";
        return null;
    };

    const isDirectorRank = (auth) => {
        const custom = (auth.customRank || "").toLowerCase();
        const progression = (auth.progression || "").toLowerCase();
        const rankingName = (auth.ranking?.name || "").toLowerCase();
        return (
            custom === "director" ||
            progression === "director" ||
            rankingName === "director"
        );
    };

    const getNameCardRankTitle = (auth) => {
        if (isDirectorRank(auth)) {
            return getDirectorDisplayTitle(auth.director) || "Sales Director";
        }

        if (auth.customRank && auth.customRank !== "none") {
            return formatRankLabel(auth.customRank);
        }

        if (auth.progression) {
            return formatRankLabel(auth.progression);
        }

        return null;
    };

    const rankTitle = getNameCardRankTitle(user.auth);

    const handleDownload = async () => {
        if (!frontCardRef.current || !backCardRef.current) return;

        // Capture front card
        const frontCanvas = await html2canvas(frontCardRef.current, {
            useCORS: true,
            backgroundColor: null,
        });
        const frontLink = document.createElement("a");
        frontLink.download = `${user.auth.name || "namecard"}_front.png`;
        frontLink.href = frontCanvas.toDataURL();
        frontLink.click();

        // Capture back card
        const backCanvas = await html2canvas(backCardRef.current, {
            useCORS: true,
            backgroundColor: null,
        });
        const backLink = document.createElement("a");
        backLink.download = `${user.auth.name || "namecard"}_back.png`;
        backLink.href = backCanvas.toDataURL();
        backLink.click();
    };

    const cardStyle = isMobile
        ? {
              width: "100%",
              height: "auto",
              aspectRatio: "800/533",
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              position: "relative",
              overflow: "hidden",
          }
        : {
              width: 800,
              height: 533,
              backgroundSize: "contain",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "center",
              position: "relative",
              overflow: "hidden",
          };

    const getTextStyle = (baseStyle) => {
        if (!isMobile) return baseStyle;

        const mobileStyle = { ...baseStyle };
        // Convert pixel values to viewport units
        mobileStyle.fontSize = `${
            (parseInt(baseStyle.fontSize) / 800) * 100
        }vw`;
        mobileStyle.left = "7.5%";

        return mobileStyle;
    };

    const desktopScaleWrap = (child) => {
        if (isMobile) return child;
        const h = 533 * desktopCardScale;
        const w = 800 * desktopCardScale;
        return (
            <div
                style={{
                    width: w,
                    height: h,
                    marginLeft: "auto",
                    marginRight: "auto",
                }}
            >
                <div
                    style={{
                        transform: `scale(${desktopCardScale})`,
                        transformOrigin: "top left",
                        width: 800,
                        height: 533,
                    }}
                >
                    {child}
                </div>
            </div>
        );
    };

    return (
        <div className="namecard-page-outer">
            <div className="namecard-container">
                <div className="namecard-header-row">
                    <button
                        type="button"
                        className="namecard-back-btn"
                        onClick={() => history.goBack()}
                        aria-label="Go back"
                    >
                        <FaArrowLeft aria-hidden />
                        Back
                    </button>
                    <button
                        type="button"
                        className="namecard-download-btn"
                        onClick={handleDownload}
                    >
                        Download
                    </button>
                </div>
                <div
                    ref={desktopScaleRef}
                    className="relative w-full mx-auto"
                >
                <div className="flex flex-col gap-5 lg:gap-6">
                    {desktopScaleWrap(
                        <div
                            ref={frontCardRef}
                            style={{
                                ...cardStyle,
                                backgroundImage: `url('${frontimage}')`,
                            }}
                        >
                        {/* Overlayed Text */}
                        <div
                            style={getTextStyle({
                                position: "absolute",
                                left: 60,
                                top: isMobile ? "22.5%" : 120,
                                color: "#e2c178",
                                fontWeight: 700,
                                fontSize: 40,
                                fontFamily: "Poppins, Arial, sans-serif",
                            })}
                        >
                            {user.auth.name && (
                                <div>
                                    {user.auth.nickname && user.auth.nickname !== "-" && (
                                        <span>
                                            {capitalizeWords(user.auth.nickname)}{" "}
                                        </span>
                                    )}
                                    {user.auth.name
                                        .split(" ")
                                        .map(
                                            (word) =>
                                                capitalizeWords(word)
                                        )
                                        .join(" ")}
                                </div>
                            )}
                        </div>
                        <div
                            style={getTextStyle({
                                position: "absolute",
                                left: 60,
                                top: isMobile ? "32%" : 170,
                                color: "#fff",
                                fontWeight: 300,
                                fontSize: 28,
                                fontFamily: "Poppins, Arial, sans-serif",
                            })}
                        >
                            {rankTitle && <div>{rankTitle}</div>}
                        </div>
                        <div
                            style={getTextStyle({
                                position: "absolute",
                                left: 60,
                                top: isMobile ? "48%" : 255,
                                color: "#fff",
                                fontWeight: 500,
                                fontSize: 30,
                                fontFamily: "Poppins, Arial, sans-serif",
                            })}
                        >
                            {user.auth.phone}
                        </div>
                        <div
                            style={getTextStyle({
                                position: "absolute",
                                left: 60,
                                top: isMobile ? "55%" : 295,
                                color: "#fff",
                                fontWeight: 500,
                                fontSize: 30,
                                fontFamily: "Poppins, Arial, sans-serif",
                            })}
                        >
                            {user.auth.email && <div>{user.auth.email}</div>}
                        </div>
                    </div>
                    )}

                    {desktopScaleWrap(
                        <div
                            ref={backCardRef}
                            style={{
                                ...cardStyle,
                                backgroundImage:
                                    "url('https://ik.imagekit.io/dnddecpho/dndream/D&D_2025NameCard_Template_4n3rVzu2O.jpg?updatedAt=1749152219160')",
                            }}
                        ></div>
                    )}
                </div>
                </div>
            </div>
        </div>
    );
};

export default withRouter(withUser(NameCard));
